import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import { DndContext, DragOverlay, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { motion } from 'framer-motion';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import "./ptecorereading.css";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);


const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const TOTAL_SECONDS = 30 * 60; // 30 minutes

const getStoredUser = (reduxUser) => {
    if (reduxUser?.result) return reduxUser.result;
    try {
        const localUser = JSON.parse(localStorage.getItem("user_info"));
        if (localUser?.result) return localUser.result;
    } catch { }
    return null;
};

const normalizeText = (s = "") => ("" + s).trim().toLowerCase();

/* ----------------------------
   Parser / Scoring
   ---------------------------- */
function parseQuestionsFromDoc(doc) {
    const questions = [];
    for (let i = 1; i <= 7; i++) {
        const qKey = `Q${i}`;
        if (!doc[qKey]) continue;

        const prompt = doc[qKey];
        const options = [];
        for (let j = 1; j <= 9; j++) {
            const optKey = `Q${i}_Opt${j}`;
            if (doc[optKey]) options.push(doc[optKey]);
        }

        const rawAns = doc[`Q${i}_Answer`];
        const explicitType = doc[`Q${i}_Type`] || doc.Questiontype || null;

        let type = "MCSA";
        if (explicitType) type = explicitType;
        else if (options.length > 1) type = (typeof rawAns === "string" && rawAns.includes(",")) ? "MCMA" : "MCSA";
        else if (/\_{3,}|___|\[__\]|\(blank\)/.test(prompt)) type = options.length > 0 ? "FILLINTHEBLANK" : "FIB-READ&WRITING";
        else if (Array.isArray(rawAns) && rawAns.length > 1 && rawAns.every(a => typeof a === "number")) type = "REORDER";
        else if (/reorder|order the sentences|reorder/i.test(prompt)) type = "REORDER";

        const q = {
            id: qKey,
            prompt,
            options,
            rawAnswer: rawAns,
            type,
            meta: { task: doc.Task, passage: doc.Passage, questiontype: doc.Questiontype }
        };

        if (type === "MCMA") {
            let ansSet = new Set();
            if (typeof rawAns === "string") rawAns.split(/[,|;]/).map(s => s.trim()).filter(Boolean).forEach(s => ansSet.add(s));
            else if (Array.isArray(rawAns)) rawAns.forEach(a => ansSet.add(a));
            else if (rawAns) ansSet.add(rawAns);
            q.answer = Array.from(ansSet);
        } else if (type === "MCSA") {
            q.answer = rawAns;
        } else if (type === "FIB-READ&WRITING") {
            if (typeof rawAns === "string") q.answer = rawAns.split(/[,|;]/).map(s => s.trim()).filter(Boolean);
            else if (Array.isArray(rawAns)) q.answer = rawAns;
            else q.answer = [];
            const blanks = (prompt.match(/___+/g) || []).length;
            q.meta.blankCount = Math.max(blanks, q.answer.length);
        } else if (type === "FILLINTHEBLANK") {
            if (typeof rawAns === "string") q.answer = rawAns.split(/[,|;]/).map(s => s.trim()).filter(Boolean);
            else q.answer = Array.isArray(rawAns) ? rawAns : [];
            q.meta.bank = options.slice();
            q.meta.blankCount = (prompt.match(/___+/g) || []).length || q.answer.length;
        } else if (type === "REORDER") {
            q.options = options.length ? options : (Array.isArray(rawAns) ? rawAns : (typeof rawAns === "string" ? rawAns.split("||") : []));
            if (Array.isArray(rawAns) && rawAns.every(n => typeof n === "number")) q.answer = rawAns;
            else q.answer = null;
        }

        questions.push(q);
    }
    return questions;
}

function scoreMCMA(question, userSelection = []) {
    const correctSet = new Set((question.answer || []).map(a => normalizeText(a)));
    let score = 0;
    userSelection.forEach(sel => {
        if (correctSet.has(normalizeText(sel))) score += 1;
        else score -= 1;
    });
    return score;
}

function scoreMCSA(question, userChoice) {
    if (!userChoice) return 0;
    return normalizeText(userChoice) === normalizeText(question.answer) ? 1 : 0;
}

function scoreFIBRW(question, userBlanks = []) {
    let s = 0;
    const answers = (question.answer || []).slice(0, userBlanks.length);
    for (let i = 0; i < userBlanks.length; i++) {
        if (!answers[i]) continue;
        if (normalizeText(userBlanks[i]) === normalizeText(answers[i])) s += 1;
    }
    return s;
}

function scoreFillInTheBlank(question, userBlanks = []) { return scoreFIBRW(question, userBlanks); }

function scoreReorder(question, userOrder = []) {
    const correct = question.answer || question.options;
    if (!correct || correct.length < 2 || !userOrder || userOrder.length < 2) return 0;
    const map = new Map();
    correct.forEach((s, idx) => map.set(normalizeText(s), idx));
    let score = 0;
    for (let i = 0; i < userOrder.length - 1; i++) {
        const ai = map.get(normalizeText(userOrder[i])) ?? -1;
        const bi = map.get(normalizeText(userOrder[i + 1])) ?? -1;
        if (ai !== -1 && bi !== -1 && bi === ai + 1) score += 1;
    }
    return score;
}

/* ----------------------------
   UI: Spinner
   ---------------------------- */
const Spinner = () => (
    <div className="spinner-overlay"><div className="spinner" /></div>
);

/* ----------------------------
   Helper: Render text with line breaks
   ---------------------------- */
const renderTextWithLineBreaks = (text) => {
    if (!text) return null;

    // Check if text contains the MCMA instruction
    const instructionRegex = /Read\s+the\s+text\s+below\s+and\s+answer\s+the\s+question\s+by\s+selecting\s+all\s+the\s+correct\s+responses\.\s+You\s+will\s+need\s+to\s+select\s+more\s+than\s+one\s+response\./gi;
    const hasInstruction = instructionRegex.test(text);

    if (hasInstruction) {
        // Reset regex
        instructionRegex.lastIndex = 0;
        const parts = text.split(instructionRegex);
        const matches = text.match(instructionRegex);

        return (
            <>
                {parts.map((part, i) => (
                    <React.Fragment key={i}>
                        {part && renderTextWithLineBreaks(part)}
                        {i < parts.length - 1 && matches && (
                            <strong style={{ display: 'block', marginBottom: '16px', marginTop: '0' }}>
                                {matches[i]}
                            </strong>
                        )}
                    </React.Fragment>
                ))}
            </>
        );
    }

    const lines = String(text).split(/\\n|\n/);
    return (
        <>
            {lines.map((line, i, arr) => (
                <React.Fragment key={i}>
                    {line}
                    {i < arr.length - 1 && <br />}
                </React.Fragment>
            ))}
        </>
    );
};

/* ----------------------------
   Question components
   ---------------------------- */
function MCMAQuestion({ q, value = [], onChange, disabled }) {
    const toggle = (option) => {
        const exists = value.includes(option);
        onChange(exists ? value.filter(v => v !== option) : [...value, option]);
    };

    const renderPrompt = (text) => {
        if (!text) return null;
        // Regex to match the phrase with flexible whitespace
        const regex = /Read\s+the\s+text\s+below\s+and\s+answer\s+the\s+question\s+by\s+selecting\s+all\s+the\s+correct\s+responses\.\s+You\s+will\s+need\s+to\s+select\s+more\s+than\s+one\s+response\./gi;

        const parts = text.split(regex);
        const matches = text.match(regex);

        if (!matches) return text;

        return (
            <>
                {parts.map((part, i) => (
                    <React.Fragment key={i}>
                        {part}
                        {i < parts.length - 1 && (
                            <>
                                <strong style={{ display: 'block', marginBottom: '12px' }}>{matches[i]}</strong>
                            </>
                        )}
                    </React.Fragment>
                ))}
            </>
        );
    };

    return (
        <div className="pte-card pte-card--question">
            <div className="pte-qtext">{renderPrompt(q.prompt)}</div>
            <div className="pte-options pte-options--vertical">
                {q.options.map((opt, idx) => (
                    <label key={idx} className="pte-option pte-option--mcma">
                        <input type="checkbox" checked={value.includes(opt)} onChange={() => toggle(opt)} disabled={disabled} />
                        <span className="pte-option-label">{opt}</span>
                    </label>
                ))}
            </div>
        </div>
    );
}

function MCSAQuestion({ q, value = "", onChange, disabled }) {
    const renderPrompt = (text) => {
        if (!text) return null;
        // Regex to match the phrase with flexible whitespace
        const regex = /Read\s+the\s+text\s+from\s+a\s+book\.\s+Answer\s+the\s+multiple\s+choice\s+question\s+by\s+selecting\s+the\s+correct\s+response\.\s+Only\s+one\s+response\s+is\s+correct\./gi;

        const parts = text.split(regex);
        const matches = text.match(regex);

        if (!matches) return text;

        return (
            <>
                {parts.map((part, i) => (
                    <React.Fragment key={i}>
                        {part}
                        {i < parts.length - 1 && <strong>{matches[i]}</strong>}
                    </React.Fragment>
                ))}
            </>
        );
    };

    return (
        <div className="pte-card pte-card--question">
            <div className="pte-qtext">{renderPrompt(q.prompt)}</div>
            <div className="pte-options pte-options--vertical">
                {q.options.map((opt, idx) => (
                    <label key={idx} className="pte-option pte-option--mcsa">
                        <input type="radio" name={q.id} checked={normalizeText(value) === normalizeText(opt)} onChange={() => onChange(opt)} disabled={disabled} />
                        <span className="pte-option-label">{opt}</span>
                    </label>
                ))}
            </div>
        </div>
    );
}

function FIBReadWrite({ q, value = [], onChange, disabled, inline = false }) {
    const parts = q.prompt.split(/(___+|\[__\]|\{__\})/g).filter(Boolean);
    const blanks = parts.filter(p => /___+|\[__\]|\{__\}/.test(p));
    const ensure = (arr, n) => { const copy = arr.slice(0, n); while (copy.length < n) copy.push(""); return copy; };
    const vals = ensure(value, blanks.length || q.meta.blankCount || 1);
    const setBlank = (idx, val) => onChange(Object.assign([], vals, { [idx]: val }));

    // Get options from q.options or q.meta.bank
    const options = q.options || q.meta?.bank || [];

    const content = (
        <>
            {parts.map((p, idx) => {
                if (/___+|\[__\]|\{__\}/.test(p)) {
                    const bi = parts.slice(0, idx + 1).filter(pp => /___+|\[__\]|\{__\}/.test(pp)).length - 1;
                    return (
                        <select
                            key={idx}
                            className="pte-inline-dropdown"
                            value={vals[bi] || ""}
                            onChange={(e) => setBlank(bi, e.target.value)}
                            disabled={disabled}
                        >
                            <option value="">Select...</option>
                            {options.map((opt, oidx) => (
                                <option key={oidx} value={opt}>
                                    {opt}
                                </option>
                            ))}
                        </select>
                    );
                }
                return <span key={idx}>{renderTextWithLineBreaks(p)}</span>;
            })}
            {inline && <span> </span>}
        </>
    );

    if (inline) return content;

    return (
        <div className="pte-card pte-card--question">
            <p className="pte-qtext-inline">
                {content}
            </p>
        </div>
    );
}

// Draggable Word Component
function DraggableWord({ id, word, disabled }) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id,
        disabled,
        data: { word }
    });

    const style = transform ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        opacity: isDragging ? 0.5 : 1,
    } : undefined;

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="pte-word"
            {...listeners}
            {...attributes}
        >
            {word}
        </div>
    );
}

// Droppable Blank Slot Component
function DroppableBlank({ id, word, onRemove, disabled }) {
    const { setNodeRef, isOver } = useDroppable({
        id,
        disabled
    });

    return (
        <span
            ref={setNodeRef}
            className={`pte-blank-slot-inline ${word ? "filled" : "empty"} ${isOver ? "drag-over" : ""}`}
        >
            {word ? (
                <span className="pte-blank-chip-inline">
                    {word}
                    {!disabled && (
                        <button
                            className="pte-chip-x-inline"
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemove();
                            }}
                        >
                            ×
                        </button>
                    )}
                </span>
            ) : (
                <span className="pte-blank-placeholder-inline">  </span>
            )}
        </span>
    );
}

function FillInTheBlankDrag({ q, value = [], onChange, disabled, inline = false, questionNumber = 1 }) {
    const bankInit = q.meta.bank || q.options || [];
    const blanksCount = q.meta.blankCount || (q.prompt.match(/___+/g) || []).length || q.answer?.length || 0;
    const [bankState, setBankState] = useState(bankInit.slice());
    const [activeId, setActiveId] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        }),
        useSensor(KeyboardSensor)
    );

    useEffect(() => setBankState(bankInit.slice()), [q.id, JSON.stringify(bankInit)]);

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);

        if (!over || disabled) return;

        const activeWord = active.data.current?.word;
        const overId = over.id;

        // Check if dropping on a blank slot
        if (overId.startsWith('blank-')) {
            const blankIndex = parseInt(overId.replace('blank-', ''));
            const newVals = [...(value || Array(blanksCount).fill(""))];
            const previous = newVals[blankIndex];

            // Set the new word in the blank
            newVals[blankIndex] = activeWord;

            // If there was a previous word, return it to the bank
            if (previous) {
                setBankState(prev => [...prev, previous]);
            }

            // Remove the word from the bank if it came from there
            if (active.id.startsWith('bank-')) {
                setBankState(prev => prev.filter((w, i) => `bank-${i}` !== active.id));
            }

            onChange(newVals);
        }
    };

    const removeFromBlank = (idx) => {
        if (disabled) return;
        const newVals = [...(value || Array(blanksCount).fill(""))];
        const removed = newVals[idx];
        newVals[idx] = "";
        if (removed) setBankState(prev => [...prev, removed]);
        onChange(newVals);
    };

    const parts = q.prompt.split(/(___+|\[__\]|\{__\})/g);
    let blankCounter = 0;

    const content = (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <div className="pte-fib-container">
                <h2 className="pte-fib-title">Fill in the Blanks – Question {questionNumber}</h2>

                <div className="pte-fib-instructions">
                    The text below is from a restaurant review in a magazine. Some of the words are missing. Drag words from the box below to the appropriate place in the text. To undo an answer choice, drag the word back to the box below the text.
                </div>

                <div className="pte-fib-passage-box">
                    {parts.map((part, idx) => {
                        if (/___+|\[__\]|\{__\}/.test(part)) {
                            const bi = blankCounter++;
                            return (
                                <DroppableBlank
                                    key={`blank-${bi}`}
                                    id={`blank-${bi}`}
                                    word={value?.[bi]}
                                    onRemove={() => removeFromBlank(bi)}
                                    disabled={disabled}
                                />
                            );
                        } else {
                            return <span key={`text-${idx}`}>{renderTextWithLineBreaks(part)}</span>;
                        }
                    })}
                </div>

                <div className="pte-fib-wordbank-notitle">
                    <div className="pte-wordbank-words">
                        {bankState.map((w, idx) => (
                            <DraggableWord
                                key={`bank-${idx}`}
                                id={`bank-${idx}`}
                                word={w}
                                disabled={disabled}
                            />
                        ))}
                        {!bankState.length && <div className="pte-muted">No words left</div>}
                    </div>
                </div>
            </div>

            <DragOverlay>
                {activeId ? (
                    <div className="pte-word" style={{ cursor: 'grabbing' }}>
                        {bankState.find((w, i) => `bank-${i}` === activeId) || ''}
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );

    if (inline) return content;

    return content;
}

function UnifiedFillInTheBlank({ questions, userAnswers, onAnswerChange, disabled }) {
    // Combine banks from all questions. Assuming they are identical or complementary.
    // We take the first one as the base.
    const bankInit = questions[0]?.meta.bank || questions[0]?.options || [];

    const [bankState, setBankState] = useState([]);
    const [activeId, setActiveId] = useState(null);

    // Initialize bank state on mount or when questions change
    useEffect(() => {
        let currentBank = [...bankInit];
        // Remove words already used in answers
        questions.forEach(q => {
            const ans = userAnswers[q.id] || [];
            ans.forEach(word => {
                if (!word) return;
                const idx = currentBank.findIndex(w => normalizeText(w) === normalizeText(word));
                if (idx !== -1) currentBank.splice(idx, 1);
            });
        });
        setBankState(currentBank);
    }, [questions, JSON.stringify(bankInit)]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        }),
        useSensor(KeyboardSensor)
    );

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);

        if (!over || disabled) return;

        const activeWord = active.data.current?.word;
        const overId = over.id; // Format: "blank|{qId}|{blankIndex}"

        if (overId.startsWith('blank|')) {
            const parts = overId.split('|');
            if (parts.length !== 3) return;
            const qId = parts[1];
            const blankIndex = parseInt(parts[2]);

            const currentVal = userAnswers[qId] || [];
            const q = questions.find(q => q.id === qId);
            const blanksCount = q.meta.blankCount || (q.prompt.match(/___+/g) || []).length || q.answer?.length || 0;

            const newVals = [...(currentVal.length ? currentVal : Array(blanksCount).fill(""))];
            while (newVals.length < blanksCount) newVals.push("");

            const previous = newVals[blankIndex];
            newVals[blankIndex] = activeWord;

            // Return previous word to bank
            if (previous) {
                setBankState(prev => [...prev, previous]);
            }

            // Remove from bank if it came from bank
            if (active.id.startsWith('bank-')) {
                setBankState(prev => prev.filter((w, i) => `bank-${i}` !== active.id));
            }

            onAnswerChange(qId, newVals);
        }
    };

    const removeFromBlank = (qId, idx) => {
        if (disabled) return;
        const currentVal = userAnswers[qId] || [];
        const newVals = [...currentVal];
        const removed = newVals[idx];
        newVals[idx] = "";

        if (removed) setBankState(prev => [...prev, removed]);
        onAnswerChange(qId, newVals);
    };

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="pte-fib-container">
                <h2 className="pte-fib-title">Fill in the Blanks</h2>
                <div className="pte-fib-instructions">
                    The text below is from a restaurant review in a magazine. Some of the words are missing. Drag words from the box below to the appropriate place in the text. To undo an answer choice, drag the word back to the box below the text.
                </div>

                <div className="pte-fib-passage-box">
                    {questions.map((q, qIdx) => {
                        const parts = q.prompt.split(/(___+|\[__\]|\{__\})/g);
                        let blankCounter = 0;
                        return (
                            <span key={q.id} className="pte-question-segment">
                                {parts.map((part, idx) => {
                                    if (/___+|\[__\]|\{__\}/.test(part)) {
                                        const bi = blankCounter++;
                                        const val = userAnswers[q.id]?.[bi];
                                        return (
                                            <DroppableBlank
                                                key={`blank|${q.id}|${bi}`}
                                                id={`blank|${q.id}|${bi}`}
                                                word={val}
                                                onRemove={() => removeFromBlank(q.id, bi)}
                                                disabled={disabled}
                                            />
                                        );
                                    } else {
                                        return <span key={`text-${idx}`}>{renderTextWithLineBreaks(part)}</span>;
                                    }
                                })}
                            </span>
                        );
                    })}
                </div>

                <div className="pte-fib-wordbank-notitle">
                    <div className="pte-wordbank-words">
                        {bankState.map((w, idx) => (
                            <DraggableWord
                                key={`bank-${idx}`}
                                id={`bank-${idx}`}
                                word={w}
                                disabled={disabled}
                            />
                        ))}
                        {!bankState.length && <div className="pte-muted">No words left</div>}
                    </div>
                </div>
            </div>
            <DragOverlay>
                {activeId ? (
                    <div className="pte-word" style={{ cursor: 'grabbing' }}>
                        {bankState.find((w, i) => `bank-${i}` === activeId) || ''}
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}

// Target Item Component (Not draggable, only selectable)
// Target Item Component (Not draggable, only selectable, but droppable for detection)
function TargetItem({ id, text, index, selected, onSelect, disabled }) {
    const { setNodeRef } = useDroppable({
        id,
        disabled
    });

    return (
        <div
            ref={setNodeRef}
            className={`pte-reorder-target-item ${selected ? 'selected' : ''}`}
            onClick={() => !disabled && onSelect && onSelect()}
        >
            <span style={{ fontWeight: 'bold', marginRight: '8px' }}>{index + 1}.</span>
            {text}
        </div>
    );
}

// Draggable Source Item Component
function SourceItem({ id, text, disabled }) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id,
        disabled,
        data: { text, fromSource: true }
    });

    const style = transform ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 999 : 'auto',
    } : undefined;

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="pte-reorder-source-item"
            {...listeners}
            {...attributes}
        >
            {text}
        </div>
    );
}

function ReorderQuestion({ q, value = [], onChange, disabled, hideInstructions = false }) {
    // Convert q.options to items with IDs and scramble them
    const initialItems = useMemo(() => {
        const s = q.options.slice();
        for (let i = s.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [s[i], s[j]] = [s[j], s[i]];
        }
        return s.map((text, idx) => ({ id: `item-${q.id}-${idx}`, text }));
    }, [q.id, q.options]);

    const [sourceItems, setSourceItems] = useState(
        initialItems.filter(item => !value.map(v => normalizeText(v)).includes(normalizeText(item.text)))
    );
    const [targetItems, setTargetItems] = useState(
        value.length ? value.map((text, idx) => ({ id: `target-${q.id}-${idx}`, text })) : []
    );
    const [activeId, setActiveId] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    useEffect(() => {
        if (!value || value.length === 0) {
            setSourceItems(initialItems.slice());
            setTargetItems([]);
        }
    }, [q.id, initialItems, value]);

    useEffect(() => {
        onChange(targetItems.map(item => item.text));
    }, [targetItems]);

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);

        if (!over) return;

        // Check if dragging from source to target
        if (active.data.current?.fromSource) {
            const sourceItem = sourceItems.find(item => item.id === active.id);
            if (sourceItem) {
                setSourceItems(prev => prev.filter(item => item.id !== active.id));
                setTargetItems(prev => [...prev, sourceItem]);
            }
        }
        // Reordering within target
        else if (active.id !== over.id) {
            setTargetItems((items) => {
                const oldIndex = items.findIndex(item => item.id === active.id);
                const newIndex = items.findIndex(item => item.id === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const { setNodeRef: setTargetRef } = useDroppable({
        id: `target-${q.id}`,
        disabled
    });

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <div className="pte-reorder-container">
                <div className="pte-reorder-two-col">
                    {/* Left Column (Source) */}
                    <div className="pte-reorder-source-col">
                        <div className="pte-reorder-col-header">Source</div>
                        <div className="pte-reorder-source-list">
                            {sourceItems.map(item => (
                                <SourceItem
                                    key={item.id}
                                    id={item.id}
                                    text={item.text}
                                    disabled={disabled}
                                />
                            ))}
                            {sourceItems.length === 0 && (
                                <div className="pte-reorder-empty">All items moved to target</div>
                            )}
                        </div >
                    </div >

                    {/* Right Column (Target) */}
                    < div ref={setTargetRef} className="pte-reorder-target-col" >
                        <div className="pte-reorder-col-header">Target</div>
                        <div className="pte-reorder-target-list">
                            {targetItems.length > 0 ? (
                                targetItems.map((item, index) => (
                                    <TargetItem
                                        key={item.id}
                                        id={item.id}
                                        text={item.text}
                                        index={index}
                                        disabled={disabled}
                                    />
                                ))
                            ) : (
                                <div className="pte-reorder-empty-target">Drag items here</div>
                            )}
                        </div>
                    </div >
                </div >
            </div >

            <DragOverlay>
                {activeId ? (
                    <div className="pte-reorder-source-item" style={{ cursor: 'grabbing' }}>
                        {sourceItems.find(item => item.id === activeId)?.text ||
                            targetItems.find(item => item.id === activeId)?.text || ''}
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext >
    );
}

// Unified Reorder Paragraph Component - Shows instructions once for all REORDER questions
function UnifiedReorderParagraph({ questions, userAnswers, onAnswerChange, disabled }) {
    // Combine all options from all questions into one pool
    const allOptions = useMemo(() => {
        return questions.reduce((acc, q) => {
            return acc.concat(q.options || []);
        }, []);
    }, [JSON.stringify(questions.map(q => q.options))]);

    // Convert to items with IDs and scramble them
    const initialItems = useMemo(() => {
        const s = allOptions.slice();
        for (let i = s.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [s[i], s[j]] = [s[j], s[i]];
        }
        return s.map((text, idx) => ({ id: `unified-item-${idx}`, text }));
    }, [allOptions]);

    // Get combined user answers from all questions
    const combinedValue = useMemo(() => {
        return questions.reduce((acc, q) => {
            const qAnswers = userAnswers[q.id] || [];
            return acc.concat(qAnswers);
        }, []);
    }, [JSON.stringify(questions.map(q => q.id)), userAnswers]);

    const [sourceItems, setSourceItems] = useState(
        initialItems.filter(item => !combinedValue.map(v => normalizeText(v)).includes(normalizeText(item.text)))
    );
    const [targetItems, setTargetItems] = useState(
        combinedValue.length ? combinedValue.map((text, idx) => ({ id: `target-unified-${idx}`, text })) : []
    );
    const [activeId, setActiveId] = useState(null);
    const [selectedId, setSelectedId] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        }),
        useSensor(KeyboardSensor)
    );

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);

        if (!over || disabled) return;

        const activeItem = sourceItems.find(item => item.id === active.id);
        if (!activeItem) return;

        // Check if dropping on target (either empty target or existing item)
        const isDropOnTarget = over.id === 'target-empty' ||
            targetItems.some(item => item.id === over.id);

        if (isDropOnTarget) {
            const newSourceItems = sourceItems.filter(item => item.id !== active.id);
            const newTargetItems = [activeItem, ...targetItems];

            setSourceItems(newSourceItems);
            setTargetItems(newTargetItems);

            // Update answers immediately after state change
            if (questions.length > 0) {
                const texts = newTargetItems.map(item => item.text);
                onAnswerChange(questions[0].id, texts);
                for (let i = 1; i < questions.length; i++) {
                    onAnswerChange(questions[i].id, []);
                }
            }
        }
    };

    const moveSelected = (direction) => {
        if (!selectedId || disabled) return;
        const index = targetItems.findIndex(item => item.id === selectedId);
        if (index === -1) return;

        const newIndex = index + direction;
        if (newIndex >= 0 && newIndex < targetItems.length) {
            const newTargetItems = arrayMove(targetItems, index, newIndex);
            setTargetItems(newTargetItems);

            // Update answers after move
            if (questions.length > 0) {
                const texts = newTargetItems.map(item => item.text);
                onAnswerChange(questions[0].id, texts);
            }
        }
    };

    // Droppable empty target component
    const EmptyTarget = () => {
        const { setNodeRef, isOver } = useDroppable({
            id: 'target-empty',
            disabled
        });

        return (
            <div
                ref={setNodeRef}
                className={`pte-reorder-empty-target ${isOver ? 'drag-over' : ''}`}
                style={isOver ? { borderColor: '#0b84b1', background: '#f0f9ff' } : {}}
            >
                Drop items here
            </div>
        );
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <div className="pte-reorder-container">
                <h2 className="pte-reorder-title">Reorder Paragraph – Question 1</h2>

                <div className="pte-reorder-instructions">
                    The text boxes on the left are sentences in a paragraph. They are in a wrong order. Put the text boxes in the correct order by dragging them from the left panel to the right panel.
                </div>

                <div className="pte-reorder-grid">
                    {/* Left Column (Source) */}
                    <div className="pte-reorder-source-col">
                        <div className="pte-reorder-col-header">Source</div>
                        <div className="pte-reorder-source-list">
                            {sourceItems.map(item => (
                                <SourceItem
                                    key={item.id}
                                    id={item.id}
                                    text={item.text}
                                    disabled={disabled}
                                />
                            ))}
                            {sourceItems.length === 0 && (
                                <div className="pte-reorder-empty">All items moved to target</div>
                            )}
                        </div>
                    </div>

                    {/* Right Column (Target) */}
                    <div className="pte-reorder-target-col">
                        <div className="pte-reorder-col-header">Target</div>
                        <div className="pte-reorder-target-box">
                            <div className="pte-reorder-target-list">
                                {targetItems.length > 0 ? (
                                    targetItems.map((item, index) => (
                                        <TargetItem
                                            key={item.id}
                                            id={item.id}
                                            text={item.text}
                                            index={index}
                                            selected={selectedId === item.id}
                                            onSelect={() => setSelectedId(item.id)}
                                            disabled={disabled}
                                        />
                                    ))
                                ) : (
                                    <EmptyTarget />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Controls Column */}
                    <div className="pte-reorder-controls-col">
                        <button
                            className="pte-arrow-btn"
                            onClick={() => moveSelected(-1)}
                            disabled={!selectedId || targetItems.findIndex(i => i.id === selectedId) === 0}
                        >
                            ↑
                        </button>
                        <button
                            className="pte-arrow-btn"
                            onClick={() => moveSelected(1)}
                            disabled={!selectedId || targetItems.findIndex(i => i.id === selectedId) === targetItems.length - 1}
                        >
                            ↓
                        </button>
                    </div>
                </div>
            </div>

            <DragOverlay>
                {activeId ? (
                    <div className="pte-reorder-source-item" style={{ cursor: 'grabbing', width: '300px' }}>
                        {sourceItems.find(item => item.id === activeId)?.text ||
                            targetItems.find(item => item.id === activeId)?.text || ''}
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}



/* ----------------------------
   Main component
   ---------------------------- */
const PTECoreReadingTest = ({ testNumber: propTestNumber = 1, isFullTest, onSectionComplete }) => {
    const { testNumber: paramTestNumber } = useParams();
    const testNumber = paramTestNumber || propTestNumber;

    const reduxUser = useSelector((state) => state?.auth?.authData ?? state?.auth);
    const currentUser = useMemo(() => getStoredUser(reduxUser), [reduxUser]);

    const [taskGroups, setTaskGroups] = useState([]);
    const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [userAnswers, setUserAnswers] = useState({});
    const [showResult, setShowResult] = useState(false);
    const [resultData, setResultData] = useState(null);
    const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
    const timerRef = useRef(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchReadingTest = async () => {
            setLoading(true);
            try {
                const url = API_BASE ? `${API_BASE}/api/ptecore/reading/test/${testNumber}` : `/api/ptecore/reading/test/${testNumber}`;
                const res = await axios.get(url);
                const docs = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);

                const groups = docs.map((d, docIdx) => {
                    const parsed = parseQuestionsFromDoc(d || {});
                    const normalized = parsed.map(p => ({
                        ...p,
                        id: `${p.id}${docs.length > 1 ? `_${docIdx}` : ""}`,
                        docIndex: docIdx
                    }));

                    if (!normalized.length) return null;

                    return {
                        task: d.Task || 1,
                        passage: d.Passage,
                        questions: normalized
                    };
                }).filter(Boolean);

                groups.sort((a, b) => {
                    const tA = parseInt(a.task) || 0;
                    const tB = parseInt(b.task) || 0;
                    return tA - tB;
                });

                setTaskGroups(groups);
                setCurrentTaskIndex(0);
                setSecondsLeft(TOTAL_SECONDS);
            } catch (err) {
                console.error("Error fetching reading test:", err);
                setTaskGroups([]);
            } finally {
                setLoading(false);
            }
        };
        fetchReadingTest();
    }, [testNumber]);

    useEffect(() => {
        if (!taskGroups.length) return;
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setSecondsLeft(s => {
                if (s <= 1) {
                    clearInterval(timerRef.current);
                    handleAutoSubmit();
                    return 0;
                }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(timerRef.current);
    }, [taskGroups]);

    const formatTime = (secs) => {
        const mm = Math.floor(secs / 60).toString().padStart(2, "0");
        const ss = Math.floor(secs % 60).toString().padStart(2, "0");
        return `${mm}:${ss}`;
    };

    const updateAnswer = (qid, value) => {
        setUserAnswers(prev => {
            const nx = { ...prev, [qid]: value };
            try {
                sessionStorage.setItem(`pte_answers_${testNumber}`, JSON.stringify(nx));
            } catch { }
            return nx;
        });
    };

    const allQuestions = taskGroups.reduce((acc, g) => acc.concat(g.questions), []);
    const currentTask = taskGroups[currentTaskIndex];

    const getCLBLevel = (scoreOutOf90) => {
        if (scoreOutOf90 >= 88) return { level: 10, range: '88-90' };
        if (scoreOutOf90 >= 78) return { level: 9, range: '78-87' };
        if (scoreOutOf90 >= 69) return { level: 8, range: '69-77' };
        if (scoreOutOf90 >= 60) return { level: 7, range: '60-68' };
        if (scoreOutOf90 >= 51) return { level: 6, range: '51-59' };
        if (scoreOutOf90 >= 42) return { level: 5, range: '42-50' };
        if (scoreOutOf90 >= 33) return { level: 4, range: '33-41' };
        if (scoreOutOf90 >= 24) return { level: 3, range: '24-32' };
        return { level: 0, range: '0-23' };
    };

    const computeScoreAndReview = () => {
        let correctCount = 0;
        const taskTypeScores = {
            'MCMA': { correct: 0, total: 0 },
            'MCSA': { correct: 0, total: 0 },
            'FIB-READ&WRITING': { correct: 0, total: 0 },
            'FILLINTHEBLANK': { correct: 0, total: 0 },
            'REORDER': { correct: 0, total: 0 }
        };

        const perQuestion = allQuestions.map((q) => {
            const user = userAnswers[q.id];
            let sc = 0;
            let isCorrect = false;

            switch (q.type) {
                case "MCMA":
                    sc = scoreMCMA(q, Array.isArray(user) ? user : []);
                    isCorrect = sc > 0;
                    if (isCorrect) taskTypeScores['MCMA'].correct++;
                    taskTypeScores['MCMA'].total++;
                    break;
                case "MCSA":
                case "MCSA1":
                case "MCSA2":
                    sc = scoreMCSA(q, user);
                    isCorrect = sc > 0;
                    if (isCorrect) taskTypeScores['MCSA'].correct++;
                    taskTypeScores['MCSA'].total++;
                    break;
                case "FIB-READ&WRITING":
                    sc = scoreFIBRW(q, Array.isArray(user) ? user : []);
                    isCorrect = sc > 0;
                    if (isCorrect) taskTypeScores['FIB-READ&WRITING'].correct++;
                    taskTypeScores['FIB-READ&WRITING'].total++;
                    break;
                case "FILLINTHEBLANK":
                    sc = scoreFillInTheBlank(q, Array.isArray(user) ? user : []);
                    isCorrect = sc > 0;
                    if (isCorrect) taskTypeScores['FILLINTHEBLANK'].correct++;
                    taskTypeScores['FILLINTHEBLANK'].total++;
                    break;
                case "REORDER":
                case "REORDER1":
                case "REORDER2":
                    sc = scoreReorder(q, Array.isArray(user) ? user : []);
                    isCorrect = sc > 0;
                    if (isCorrect) taskTypeScores['REORDER'].correct++;
                    taskTypeScores['REORDER'].total++;
                    break;
                default:
                    sc = 0;
            }
            if (isCorrect) correctCount++;
            return { id: q.id, prompt: q.prompt, type: q.type, correct: q.answer, user, score: sc };
        });

        // Calculate score out of 90
        const totalQuestions = allQuestions.length;
        const scoreOutOf90 = totalQuestions > 0
            ? Math.round((correctCount / totalQuestions) * 90)
            : 0;

        // Get CLB level
        const clbData = getCLBLevel(scoreOutOf90);

        // Calculate task-wise percentages
        const taskBreakdown = Object.keys(taskTypeScores).map(type => ({
            type,
            correct: taskTypeScores[type].correct,
            total: taskTypeScores[type].total,
            percentage: taskTypeScores[type].total > 0
                ? Math.round((taskTypeScores[type].correct / taskTypeScores[type].total) * 100)
                : 0
        })).filter(t => t.total > 0); // Only include task types that exist in the test

        return {
            total: correctCount,
            totalQuestions,
            scoreOutOf90,
            clbLevel: clbData.level,
            clbRange: clbData.range,
            taskBreakdown,
            perQuestion
        };
    };

    const doSubmit = async (auto = false) => {
        setSubmitting(true);
        setSaving(true);
        const elapsed = Math.max(0, TOTAL_SECONDS - secondsLeft);
        const payload = {
            testNumber,
            userId: currentUser?.id || currentUser?._id || null,
            answers: userAnswers,
            timeTakenSeconds: elapsed,
            autoSubmitted: auto
        };
        try {
            const res = await axios.post(`${API_BASE || ""}/api/submit-test`, payload);
            const localResult = computeScoreAndReview();
            setResultData({
                apiResponse: res.data,
                local: localResult,
                timeTakenSeconds: elapsed
            });
            setShowResult(true);
            if (timerRef.current) clearInterval(timerRef.current);
        } catch (err) {
            console.error("Submit error:", err);
            const localResult = computeScoreAndReview();
            setResultData({
                apiResponse: { error: "server_error" },
                local: localResult,
                timeTakenSeconds: elapsed
            });
            setShowResult(true);
        } finally {
            setSaving(false);
            setSubmitting(false);
        }
    };
    const handleAutoSubmit = () => doSubmit(true);
    const handleManualSubmit = () => doSubmit(false);

    const answeredCount = allQuestions.reduce((c, q) => {
        const a = userAnswers[q.id];
        if (Array.isArray(a)) return c + (a.filter(Boolean).length > 0 ? 1 : 0);
        return c + (a ? 1 : 0);
    }, 0);

    if (loading) return (<div className="container-centered"><Spinner /><div className="loading-msg">Loading reading test...</div></div>);
    if (!currentTask) return (<div className="container-centered"><div className="loading-msg">No tasks available</div></div>);

    const isSingleColumn = currentTask && currentTask.questions.some(q => q.type === "FIB-READ&WRITING" || q.type === "FILLINTHEBLANK" || q.type === "REORDER");
    const shouldNavBeTop = currentTask && currentTask.questions.some(q => q.type === "MCMA" || q.type === "REORDER");

    return (
        <div className="ptest-root">
            {(loading || saving || submitting) && <Spinner />}

            <header className="ptest-header">
                <div className="ptest-title">
                    <h1>PTE Core Reading Test {testNumber}</h1>
                    <div className="progress-indicator">Task {currentTaskIndex + 1} of {taskGroups.length} • Answered {answeredCount}/{allQuestions.length}</div>
                </div>

                <div className="right-controls">
                    <div className={secondsLeft <= 5 * 60 ? "timer timer-warning" : "timer"}><span className="timer-emoji">⏱</span> {formatTime(secondsLeft)}</div>
                </div>
            </header>

            <main className={`ptest-main ${isSingleColumn ? "ptest-main--single-col" : "ptest-main--two-col"}`}>
                {!showResult && currentTask && (
                    <>
                        {!isSingleColumn && !currentTask.questions.some(q => q.type === "REORDER") && (
                            <section className="passage-wrapper">
                                <div className="passage-outer">
                                    <div className="passage-inner">
                                        <h2 className="passage-title">Reading Passage - Task {currentTask.task}</h2>
                                        <div className="passage-body">{renderTextWithLineBreaks(currentTask.passage)}</div>
                                    </div>
                                </div>
                            </section>
                        )}

                        <section className={`questions-wrapper ${shouldNavBeTop ? "mobile-nav-top" : ""}`}>

                            {isSingleColumn ? (
                                currentTask.questions.some(q => q.type === "REORDER") ? (
                                    <UnifiedReorderParagraph
                                        questions={currentTask.questions.filter(q => q.type === "REORDER")}
                                        userAnswers={userAnswers}
                                        onAnswerChange={updateAnswer}
                                        disabled={showResult}
                                    />
                                ) : (
                                    <section className="passage-wrapper">
                                        <div className="passage-outer">
                                            <div className="passage-inner">
                                                <h2 className="passage-title">Reading Passage - Task {currentTask.task}</h2>
                                                <div className="pte-text-flow">
                                                    {currentTask.questions.some(q => q.type === "FILLINTHEBLANK") ? (
                                                        <>
                                                            <UnifiedFillInTheBlank
                                                                questions={currentTask.questions.filter(q => q.type === "FILLINTHEBLANK")}
                                                                userAnswers={userAnswers}
                                                                onAnswerChange={updateAnswer}
                                                                disabled={showResult}
                                                            />
                                                            {currentTask.questions.filter(q => q.type !== "FILLINTHEBLANK").map((q, idx) => (
                                                                <FIBReadWrite key={`${q.id}-${idx}`} q={q} value={userAnswers[q.id] || []} onChange={(v) => updateAnswer(q.id, v)} disabled={showResult} inline={true} />
                                                            ))}
                                                        </>
                                                    ) : (
                                                        currentTask.questions.map((q, idx) => {
                                                            return <FIBReadWrite key={`${q.id}-${idx}`} q={q} value={userAnswers[q.id] || []} onChange={(v) => updateAnswer(q.id, v)} disabled={showResult} inline={true} />;
                                                        })
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                )
                            ) : (
                                currentTask.questions.some(q => q.type === "REORDER") ? (
                                    <>
                                        <UnifiedReorderParagraph
                                            questions={currentTask.questions.filter(q => q.type === "REORDER")}
                                            userAnswers={userAnswers}
                                            onAnswerChange={updateAnswer}
                                            disabled={showResult}
                                        />
                                        {currentTask.questions.filter(q => q.type !== "REORDER").map((q, idx) => (
                                            <div key={`${q.id}-${idx}`} className="question-block">
                                                <div className="q-meta"><span className="q-type">{q.type}</span><span className="q-id">{q.id}</span></div>
                                                <div className="question-render">
                                                    {q.type === "MCMA" && <MCMAQuestion q={q} value={userAnswers[q.id] || []} onChange={(v) => updateAnswer(q.id, v)} disabled={showResult} />}
                                                    {q.type === "MCSA" && <MCSAQuestion q={q} value={userAnswers[q.id] || ""} onChange={(v) => updateAnswer(q.id, v)} disabled={showResult} />}
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                ) : (
                                    currentTask.questions.map((q, idx) => (
                                        <div key={`${q.id}-${idx}`} className="question-block">
                                            <div className="q-meta"><span className="q-type">{q.type}</span><span className="q-id">{q.id}</span></div>

                                            <div className="question-render">
                                                {q.type === "MCMA" && <MCMAQuestion q={q} value={userAnswers[q.id] || []} onChange={(v) => updateAnswer(q.id, v)} disabled={showResult} />}
                                                {q.type === "MCSA" && <MCSAQuestion q={q} value={userAnswers[q.id] || ""} onChange={(v) => updateAnswer(q.id, v)} disabled={showResult} />}
                                                {q.type === "FIB-READ&WRITING" && <FIBReadWrite q={q} value={userAnswers[q.id] || []} onChange={(v) => updateAnswer(q.id, v)} disabled={showResult} />}
                                                {q.type === "FILLINTHEBLANK" && <FillInTheBlankDrag q={q} value={userAnswers[q.id] || []} onChange={(v) => updateAnswer(q.id, v)} disabled={showResult} />}
                                                {q.type === "REORDER" && <ReorderQuestion q={q} value={userAnswers[q.id] || []} onChange={(v) => updateAnswer(q.id, v)} disabled={showResult} />}
                                            </div>
                                        </div>
                                    ))
                                )
                            )}

                            <div className="ptest-header" style={{ marginTop: '20px' }}>
                                <div></div>
                                <div className="right-controls flex gap-4">
                                    <button className="btn btn-ghost" onClick={() => { if (currentTaskIndex > 0) setCurrentTaskIndex(currentTaskIndex - 1); }} disabled={currentTaskIndex === 0}>← Previous Task</button>
                                    {currentTaskIndex < taskGroups.length - 1 ? (
                                        <button className="btn btn-primary" onClick={() => setCurrentTaskIndex(currentTaskIndex + 1)}>Next Task →</button>
                                    ) : (
                                        <button className="btn btn-submit" onClick={handleManualSubmit} disabled={submitting}>Submit Test</button>
                                    )}
                                </div>
                            </div>
                        </section>
                    </>
                )}

                {showResult && resultData && (
                    <section className="result-section">
                        <h2>Results</h2>
                        <div className="result-summary">
                            <div className="result-card">
                                <h3>PTE Core Score</h3>
                                <div className="score-display">
                                    <span className="score-value">{resultData.local.scoreOutOf90}</span>
                                    <span className="score-total">/ 90</span>
                                </div>
                            </div>
                            <div className="result-card">
                                <h3>CLB Level</h3>
                                <div className="clb-display">
                                    <span className="clb-value">{resultData.local.clbLevel}</span>
                                    <span className="clb-range">({resultData.local.clbRange})</span>
                                </div>
                            </div>
                            <div className="result-card result-card--stats">
                                <h3>Summary</h3>
                                <div className="result-stats">
                                    <div><strong>Correct Answers:</strong> {resultData.local.total} / {resultData.local.totalQuestions}</div>
                                    <div><strong>Time Taken:</strong> {Math.floor(resultData.timeTakenSeconds / 60)}m {resultData.timeTakenSeconds % 60}s</div>
                                </div>
                            </div>
                        </div>

                        {resultData.local.taskBreakdown && resultData.local.taskBreakdown.length > 0 && (() => {
                            const taskLabels = resultData.local.taskBreakdown.map(t => t.type);
                            const taskPercentages = resultData.local.taskBreakdown.map(t => t.percentage);
                            const taskCorrect = resultData.local.taskBreakdown.map(t => t.correct);
                            const taskTotal = resultData.local.taskBreakdown.map(t => t.total);

                            const barChartData = {
                                labels: taskLabels,
                                datasets: [{
                                    label: 'Performance (%)',
                                    data: taskPercentages,
                                    backgroundColor: [
                                        'rgba(11, 132, 177, 0.8)',
                                        'rgba(22, 163, 74, 0.8)',
                                        'rgba(234, 179, 8, 0.8)',
                                        'rgba(239, 68, 68, 0.8)',
                                        'rgba(168, 85, 247, 0.8)'
                                    ],
                                    borderColor: [
                                        'rgba(11, 132, 177, 1)',
                                        'rgba(22, 163, 74, 1)',
                                        'rgba(234, 179, 8, 1)',
                                        'rgba(239, 68, 68, 1)',
                                        'rgba(168, 85, 247, 1)'
                                    ],
                                    borderWidth: 2,
                                    borderRadius: 8,
                                }]
                            };

                            const doughnutData = {
                                labels: taskLabels,
                                datasets: [{
                                    data: taskCorrect,
                                    backgroundColor: [
                                        'rgba(11, 132, 177, 0.8)',
                                        'rgba(22, 163, 74, 0.8)',
                                        'rgba(234, 179, 8, 0.8)',
                                        'rgba(239, 68, 68, 0.8)',
                                        'rgba(168, 85, 247, 0.8)'
                                    ],
                                    borderColor: '#fff',
                                    borderWidth: 3,
                                }]
                            };

                            const barOptions = {
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: { display: false },
                                    title: { display: false },
                                    tooltip: {
                                        callbacks: {
                                            label: function (context) {
                                                const idx = context.dataIndex;
                                                return `${context.parsed.y}% (${taskCorrect[idx]}/${taskTotal[idx]} correct)`;
                                            }
                                        }
                                    }
                                },
                                scales: {
                                    y: {
                                        beginAtZero: true,
                                        max: 100,
                                        ticks: {
                                            callback: function (value) {
                                                return value + '%';
                                            }
                                        },
                                        grid: {
                                            color: 'rgba(0, 0, 0, 0.05)'
                                        }
                                    },
                                    x: {
                                        grid: {
                                            display: false
                                        }
                                    }
                                },
                                animation: {
                                    duration: 1500,
                                    easing: 'easeOutQuart'
                                }
                            };

                            const doughnutOptions = {
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: {
                                        position: 'bottom',
                                        labels: {
                                            padding: 15,
                                            font: { size: 12 }
                                        }
                                    },
                                    tooltip: {
                                        callbacks: {
                                            label: function (context) {
                                                const idx = context.dataIndex;
                                                return `${taskLabels[idx]}: ${taskCorrect[idx]}/${taskTotal[idx]} (${taskPercentages[idx]}%)`;
                                            }
                                        }
                                    }
                                },
                                animation: {
                                    animateRotate: true,
                                    animateScale: true,
                                    duration: 1500,
                                    easing: 'easeOutQuart'
                                }
                            };

                            return (
                                <motion.div
                                    className="task-performance"
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                >
                                    <h3>Task-Wise Performance</h3>

                                    <div className="charts-grid">
                                        <motion.div
                                            className="chart-container"
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ duration: 0.5, delay: 0.4 }}
                                        >
                                            <h4 className="chart-title">Performance by Task Type</h4>
                                            <div style={{ height: '300px' }}>
                                                <Bar data={barChartData} options={barOptions} />
                                            </div>
                                        </motion.div>

                                        <motion.div
                                            className="chart-container"
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ duration: 0.5, delay: 0.6 }}
                                        >
                                            <h4 className="chart-title">Correct Answers Distribution</h4>
                                            <div style={{ height: '300px' }}>
                                                <Doughnut data={doughnutData} options={doughnutOptions} />
                                            </div>
                                        </motion.div>
                                    </div>

                                    <motion.div
                                        className="task-performance-grid"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ duration: 0.5, delay: 0.8 }}
                                    >
                                        {resultData.local.taskBreakdown.map((task, idx) => (
                                            <motion.div
                                                key={task.type}
                                                className="task-performance-item"
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.4, delay: 0.9 + (idx * 0.1) }}
                                            >
                                                <div className="task-performance-header">
                                                    <span className="task-type-label">{task.type}</span>
                                                    <span className="task-percentage">{task.percentage}%</span>
                                                </div>
                                                <div className="task-performance-bar-bg">
                                                    <motion.div
                                                        className="task-performance-bar"
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${task.percentage}%` }}
                                                        transition={{ duration: 1.5, delay: 1 + (idx * 0.1), ease: "easeOut" }}
                                                    />
                                                </div>
                                                <div className="task-performance-stats">
                                                    {task.correct} / {task.total} correct
                                                </div>
                                            </motion.div>
                                        ))}
                                    </motion.div>
                                </motion.div>
                            );
                        })()}

                        <div className="review-list">
                            <h3>Detailed Review</h3>
                            {resultData.local.perQuestion.map((pq, idx) => (
                                <div key={pq.id} className="review-item">
                                    <div className="review-head"><strong>{idx + 1}. {pq.id}</strong><span className="score-pill">Score: {pq.score}</span></div>
                                    <div className="review-body">
                                        <div className="review-prompt">{renderTextWithLineBreaks(pq.prompt)}</div>
                                        <div className="review-answers">
                                            <div><strong>Your:</strong> {Array.isArray(pq.user) ? (pq.user.length ? pq.user.join(" | ") : "—") : (pq.user ? String(pq.user) : "—")}</div>
                                            <div><strong>Correct:</strong> {Array.isArray(pq.correct) ? (pq.correct.length ? pq.correct.join(" | ") : "—") : (pq.correct ? String(pq.correct) : "—")}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="result-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                            <button className="btn btn-primary" onClick={() => { setShowResult(false); setResultData(null); setUserAnswers({}); setCurrentTaskIndex(0); setSecondsLeft(TOTAL_SECONDS); }}>Retake Test</button>
                            {isFullTest && onSectionComplete && (
                                <button
                                    className="btn btn-primary"
                                    style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
                                    onClick={onSectionComplete}
                                >
                                    Proceed to Listening Section
                                </button>
                            )}
                        </div>
                    </section>
                )}
            </main>
        </div>
    );
};

export default PTECoreReadingTest;
